import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IListColumnConfig } from '@datachain/ui-sdk/common/base/list-elements.interface';
import { Vault } from '@ultimate/vault';
import { Set } from 'immutable';

export type StoredColumn = {
	dataType: string;
	dataField: string;
	fixed: boolean;
	name: string;
	visible: boolean;
	visibleIndex: number;
	filterType: string;
	filterValue: Array<string | number>; // FIXME: recursive array
	filterValues: Array<string | number>;
	fixedPosition: boolean;
	selectedFilterOperation: string;
	groupIndex: number;
	sortIndex: number;
	sortOrder: 'asc' | 'desc';
	width: number | string;
};
export type IDxList = {
	allowedPageSizes: Array<number>;
	columns: Array<Partial<StoredColumn>>;
	filterPanel: { filterEnabled: true };
	filterValue: Array<string>;
	pageIndex: number;
	pageSize: number;
	searchText: string;
	selectedRowKeys: Array<number | string>;
};

const TRACKED_URL_SEGMENTS = ['organization', 'modules'];

@Injectable({
	providedIn: 'root',
})
export class DxListsStorage extends Vault {
	private readonly router = inject(Router);
	private trackedLists = Set<string>();
	public constructor() {
		super({
			type: 'local',
		});
	}

	public setListState(listKey: string, state: IDxList): void {
		const listKeyWithPrefix = this.generateListKeyWithPrefix(listKey);

		if (!this.trackedLists.has(listKeyWithPrefix)) {
			this.trackedLists = this.trackedLists.add(listKeyWithPrefix);
		}
		this.set<IDxList>(listKeyWithPrefix, state);
	}

	public getListState(listKey: string): IDxList | undefined {
		const listKeyWithPrefix = this.generateListKeyWithPrefix(listKey);

		const maybe = this.trackedLists.get(listKeyWithPrefix);
		const data = this.getList(listKeyWithPrefix);
		if (!maybe && !data) {
			// eslint-disable-next-line no-console
			console.warn(
				'%c Local storage ',
				'color: #4CAF50; background-color: black; font-weight: bold',
				'no data found and not tracked yet for: ',
				listKey,
			);
		}
		return data;
	}

	public synchronize<T extends IListColumnConfig<U>, U = unknown>(
		listKey: string,
		columns: Array<T>,
	): Array<T> {
		const listKeyWithPrefix = this.generateListKeyWithPrefix(listKey);

		const localStorageListState = this.getList(listKeyWithPrefix);
		if (localStorageListState === undefined) {
			return columns;
		}
		return columns.map((generated) => {
			if (!localStorageListState.columns) {
				return generated;
			}

			const columnInLocalStorage = localStorageListState.columns.find(
				(c) => c.dataField === generated.field,
			);

			if (!columnInLocalStorage) {
				return generated;
			}
			return {
				...generated,
				visible: columnInLocalStorage.visible,
				groupIdx: columnInLocalStorage.groupIndex,
				['selectedFilterOperation']:
					columnInLocalStorage.selectedFilterOperation,
				['filterValue']: columnInLocalStorage.filterValue,
				['filterValues']: columnInLocalStorage.filterValues,
				['width']: columnInLocalStorage.width,
				['sortIndex']: columnInLocalStorage.sortIndex,
				['sortOrder']: columnInLocalStorage.sortOrder,
			};
		});
	}

	public sanitize(listKey: string): void {
		const listConfig = this.getList(listKey);
		if (!listConfig) {
			return;
		}
		const updatedList = {
			...listConfig,
			selectedRowKeys: [],
		};
		this.setListState(listKey, updatedList);
	}

	private getList(listKey: string): IDxList | undefined {
		return this.get<IDxList>(listKey);
	}

	private generateListKeyWithPrefix(listKey: string): string {
		const urlTree = this.router.parseUrl(this.router.url);

		if (!urlTree) {
			return '';
		}
		const urlSegments = urlTree.root.children['primary'].segments;

		const routePrefixes = TRACKED_URL_SEGMENTS.reduce((acc, curr) => {
			const index = urlSegments.findIndex((segment) => segment.path === curr);

			if (index !== -1 && index + 1 < urlSegments.length) {
				acc.push(`${curr}__${urlSegments[index + 1].path}`);
			}

			return acc;
		}, new Array<string>());

		const prefix = routePrefixes.join('__');

		return prefix ? `${prefix}__${listKey}` : listKey;
	}
}
